import type { Uuid } from "spacetimedb";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { Redirect } from "@/web/components/Redirect";
import { Button } from "@/web/components/primitives/button";
import { Spinner } from "@/web/components/primitives/spinner";
import { Textarea } from "@/web/components/primitives/textarea";
import { toaster } from "@/web/components/primitives/toast";
import { DefaultNavbar } from "@/web/components/styled/AppNavbar";
import {
  ComposedBlocks,
  previewCommunityQuotes,
  type BlockRenderContext,
} from "@/web/components/styled/ComposedBlocks";
import { ComposedTestimonialsPreview } from "@/web/components/styled/ComposedTestimonials";
import {
  type PageBlock,
  parseBlocksPayload,
  serializeBlocks,
  splitProseAndBody,
} from "@/web/lib/composerBlocks";
import { parseEmbedUrl, safeEmbedUrl } from "@/web/lib/composerEmbeds";
import { sanitizeComposerHtml } from "@/web/lib/composerSanitize";
import { COMPOSER_STARTER_HTML } from "@/web/lib/composerStarter";
import { pickSuggestions } from "@/web/lib/composerSuggestions";
import { env } from "@/web/lib/env";
import { ErrorHandler } from "@/web/lib/errors";
import { useFeatureFlag } from "@/web/lib/featureFlags";
import { cn } from "@/web/lib/styling";
import { tableQueryOptions, useProcedureMutation, useProcedureQueryLazy } from "@/web/lib/tanstack";
import { procedures, tables } from "@tycho/web-bindings";
import { useSuspenseQuery } from "@tanstack/react-query";

import { ComposerTour } from "./-ComposerTour";
import { MESSAGE_KEYS } from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/messageKeys";

export const Route = createFileRoute("/_protected/profile/compose")({
  component: ComposeRoute,
});

/**
 * One chat turn.
 *
 * The composer is a back-and-forth: an assistant turn carries the conversational
 * `content` shown as a chat bubble, plus (only on turns that built or updated the
 * page) the `blocks` — the ordered page-block array (see `composerBlocks.ts`)
 * rendered in the preview. `blocks` is undefined on conversation-only turns
 * (questions, suggestions).
 *
 * The backend expects the full conversation on each call so it can make
 * incremental edits. When we replay an assistant turn back to the model we
 * reconstruct the fenced+sentinel form it emits (prose + serialized blocks) via
 * `assistantTurnForModel`, so the model sees its own prior output verbatim.
 *
 * `attachments` are image tokens referenced by a user turn. The model is told
 * to embed `{{img:<id>}}` for each; the tokens resolve to browser-local `blob:`
 * URLs only in this session (rewritten to CDN URLs on Save).
 */
type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  blocks?: PageBlock[];
  attachments?: LocalAsset[];
  /**
   * Marks an assistant bubble that reports a failed generation (e.g. the OpenAI
   * call timed out) rather than a real model reply. Rendered with an error style
   * + a "Try again" button, and never sent back to the model on replay.
   */
  error?: boolean;
  /**
   * Marks the seeded greeting turn, which carries the deterministic starter page
   * as its `blocks`. Used only to suppress the "Updated the preview →" note (the
   * starter isn't a model build). It's still replayed to the model like any other
   * assistant turn, so the model sees the starter as the page-so-far.
   */
  isGreeting?: boolean;
};

/**
 * The greeting seeded into the chat before the first exchange. Rendered as an
 * assistant bubble and, once the owner replies, sent to the model as the opening
 * assistant turn (and persisted as the chat's first turn) so the model sees the
 * question it "asked". Personalized with the owner's first name when we have it.
 *
 * It also carries the deterministic STARTER PAGE as its `blocks` (a single `html`
 * block wrapping `COMPOSER_STARTER_HTML`), so a fresh session lands on a
 * populated preview instead of a blank canvas (owners find a blank page
 * intimidating). Because `assistantTurnForModel` replays this turn's prose +
 * serialized blocks, the model receives the starter as the page-so-far and (per
 * the system prompt) makes incremental edits to it. The bubble itself shows only
 * the greeting prose — the "Updated the preview →" note is suppressed for the
 * greeting so it doesn't read as if the assistant just built something (see
 * AssistantBubble usage). The greeting is silently seeded: no extra bubble.
 */
function greetingMessage(firstName: string | null | undefined): ChatMessage {
  const name = firstName?.trim();
  const hi = name ? `Hey ${name}` : "Hey there";
  return {
    role: "assistant",
    content: `${hi} — I'm an AI assistant that can help you show your best self to potential investors. I've started you off with a simple template on the left — we'll shape it together. To get going, tell me a little about your goals: are you looking to buy your current building, move to a new one, or something else?`,
    blocks: [{ type: "html", html: COMPOSER_STARTER_HTML }],
    isGreeting: true,
  };
}

/**
 * Reconstruct the completion the model originally emitted for an assistant turn,
 * so replaying the conversation feeds the model its own prior output (prose + the
 * serialized page blocks) verbatim. Mirrors the backend's `parseCompletion` /
 * block serializer in reverse.
 */
function assistantTurnForModel(message: ChatMessage): string {
  const content = message.content ?? "";
  const body = message.blocks && message.blocks.length > 0 ? serializeBlocks(message.blocks) : "";
  if (!body) return content;
  const prose = content.trim();
  return prose.length > 0 ? `${prose}\n\n${body}` : body;
}

/**
 * Reverse of {@link assistantTurnForModel}: split a persisted assistant turn
 * (prose + optional serialized block body) back into a `ChatMessage`. Mirrors the
 * backend split so a rehydrated conversation reconstructs the same bubbles +
 * preview it had before the refresh.
 */
function parsePersistedAssistantTurn(raw: string): ChatMessage {
  const { prose, body } = splitProseAndBody(raw);
  const blocks = body ? parseBlocksPayload(body) : [];
  return {
    role: "assistant",
    content: prose,
    blocks: blocks.length > 0 ? blocks : undefined,
  };
}

/** Turn a persisted transcript (from `getComposerChat`) into display bubbles. */
function messagesFromPersisted(persisted: { role: string; content: string }[]): ChatMessage[] {
  return persisted.map((turn, index) => {
    if (turn.role !== "assistant") return { role: "user", content: turn.content };
    const parsed = parsePersistedAssistantTurn(turn.content);
    // The chat's first assistant turn is always the seeded greeting, which
    // carries the starter page as html. Re-mark it so the restored bubble
    // suppresses the "Updated the preview →" note, matching a fresh session.
    return index === 0 ? { ...parsed, isGreeting: true } : parsed;
  });
}

/** A browser-local media file (image OR video) dropped into the chat. `token`
 * is the stable `{{img:<id>}}` / `{{video:<id>}}` handle the LLM embeds;
 * `objectUrl` is the in-memory preview source we swap it for at render time;
 * `file` is kept so Save can upload the bytes to GCS. */
type LocalAsset = {
  id: string;
  kind: "image" | "video";
  token: string;
  objectUrl: string;
  fileName: string;
  file: File;
};

/** A pasted YouTube/Vimeo embed. `token` is `{{embed:<id>}}`; `embedUrl` is the
 * normalized, allowlisted iframe URL the preview + public page resolve to. */
type LocalEmbed = {
  id: string;
  token: string;
  provider: "youtube" | "vimeo";
  embedUrl: string;
};

function makeLocalId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function makeLocalAsset(file: File): LocalAsset {
  const id = makeLocalId();
  const kind: LocalAsset["kind"] = file.type.startsWith("video/") ? "video" : "image";
  return {
    id,
    kind,
    token: `{{${kind === "video" ? "video" : "img"}:${id}}}`,
    objectUrl: URL.createObjectURL(file),
    fileName: file.name,
    file,
  };
}

/**
 * Resolve media tokens for the composer PREVIEW body:
 * - `{{img:<id>}}` / `{{video:<id>}}` → the browser-local `blob:` URL.
 * Tokens with no known asset (e.g. a token restored after a refresh) are left
 * as-is. Embeds are handled separately by {@link extractPreviewEmbeds}.
 */
function resolveMediaTokens(html: string, assets: Map<string, LocalAsset>): string {
  return html.replace(/\{\{(?:img|video):([^}]+)\}\}/g, (match, id: string) => {
    return assets.get(id)?.objectUrl ?? match;
  });
}

/** True if `html` references a media token whose asset isn't in memory (e.g. a
 * page restored from the transcript after a refresh — the original files are
 * gone). Used to fall back to the last saved page for preview + Save. */
function hasUnresolvableMediaTokens(html: string, assets: Map<string, LocalAsset>): boolean {
  for (const [, id] of html.matchAll(/\{\{(?:img|video):([^}]+)\}\}/g)) {
    if (id && !assets.has(id)) return true;
  }
  return false;
}

/**
 * Pull every `{{embed:<id-or-url>}}` out of the preview HTML (and the `<iframe>`
 * wrapping it, if present), returning the HTML with those tokens removed plus the
 * resolved, allowlisted embed URLs. Session embeds resolve via `embeds` (the
 * `{{embed:<localId>}}` form); a page restored from a saved draft carries the
 * `{{embed:<embedUrl>}}` form, re-validated through `safeEmbedUrl`.
 *
 * Embeds are rendered as trusted React iframes OUTSIDE the sandboxed preview
 * (mirroring the public `/ttw/<cardId>` page), so the preview iframe never needs
 * `allow-scripts` — the model's HTML can't execute in the app origin.
 */
function extractPreviewEmbeds(
  html: string,
  embeds: Map<string, LocalEmbed>,
): { html: string; embedUrls: string[] } {
  const embedUrls: string[] = [];
  const push = (idOrUrl: string) => {
    const url = embeds.get(idOrUrl)?.embedUrl ?? safeEmbedUrl(idOrUrl);
    if (url && !embedUrls.includes(url)) embedUrls.push(url);
  };
  const withoutIframes = html.replace(
    /<iframe[^>]*src=["']?\{\{embed:([^}"']+)\}\}["']?[^>]*>\s*<\/iframe>/gi,
    (_match, id: string) => {
      push(id);
      return "";
    },
  );
  const cleaned = withoutIframes.replace(/\{\{embed:([^}]+)\}\}/g, (_match, id: string) => {
    push(id);
    return "";
  });
  return { html: cleaned, embedUrls };
}

/** Client-side video size cap; mirrors the backend signed-range max. */
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

type CreateAssetUploadUrl = (args: { contentType: string }) => Promise<{
  uploadUrl: string;
  objectPath: string;
  contentType: string;
  contentLengthRange: string;
}>;

/**
 * Prepare the page HTML for a public Save: upload each browser-local asset that
 * the HTML references to GCS and swap its token for the CDN URL; swap each
 * `{{embed:<localId>}}` for `{{embed:<embedUrl>}}` (a self-describing form the
 * public route resolves). Only uploads assets actually referenced in the page.
 * Throws if an upload fails, so Save aborts rather than persisting a broken page.
 */
async function uploadAndRewriteTokens(
  rawHtml: string,
  assets: Map<string, LocalAsset>,
  embeds: Map<string, LocalEmbed>,
  createAssetUploadUrl: CreateAssetUploadUrl,
): Promise<string> {
  let html = rawHtml;

  // Rewrite embed tokens first (no upload needed).
  html = html.replace(/\{\{embed:([^}]+)\}\}/g, (match, id: string) => {
    const embed = embeds.get(id);
    return embed ? `{{embed:${embed.embedUrl}}}` : match;
  });

  // Collect the referenced asset ids (media only), upload each once.
  const referenced = new Set<string>();
  for (const [, id] of html.matchAll(/\{\{(?:img|video):([^}]+)\}\}/g)) {
    if (id) referenced.add(id);
  }

  const cdnByToken = new Map<string, string>();
  for (const id of referenced) {
    const asset = assets.get(id);
    if (!asset) continue; // no in-memory file (e.g. post-refresh); guarded below
    const { uploadUrl, objectPath, contentType, contentLengthRange } = await createAssetUploadUrl({
      contentType: asset.file.type || (asset.kind === "video" ? "video/mp4" : "image/jpeg"),
    });
    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "content-type": contentType,
        "x-goog-content-length-range": contentLengthRange,
      },
      body: asset.file,
    });
    if (!putRes.ok) {
      const text = await putRes.text().catch((e: unknown) => {
        ErrorHandler.reportError(e);
        return "";
      });
      throw new Error(`Asset upload failed (${putRes.status}): ${text}`);
    }
    cdnByToken.set(asset.token, `${env.VITE_WITHCO_IMAGE_CDN_BASE}/${objectPath}`);
  }

  html = html.replace(/\{\{(?:img|video):[^}]+\}\}/g, (token) => cdnByToken.get(token) ?? token);

  // Any media token still present had no in-memory `File` to upload — this
  // happens after a refresh, where the rehydrated transcript restores HTML with
  // the original `{{img:…}}` / `{{video:…}}` tokens but the browser-local blobs
  // are gone. The backend rejects leftover media tokens, so surface a clear,
  // actionable message here rather than letting the save fail cryptically.
  if (/\{\{(?:img|video):/.test(html)) {
    throw new Error(
      "Some media from an earlier session can't be re-uploaded. Re-attach it, then save again.",
    );
  }

  return html;
}

// ---------------------------------------------------------------------------
// Block-array orchestration: the media/embed pipeline above operates on ONE html
// string; these apply it across the `html` blocks of a page (typed blocks carry
// no HTML and pass through untouched).
// ---------------------------------------------------------------------------

/** True if ANY `html` block references a media token whose asset isn't in memory
 * (e.g. a page restored from the transcript after a refresh — the original files
 * are gone). Drives the fallback to the last saved page for preview + Save. */
function blocksHaveUnresolvableMedia(
  blocks: PageBlock[],
  assets: Map<string, LocalAsset>,
): boolean {
  return blocks.some(
    (block) => block.type === "html" && hasUnresolvableMediaTokens(block.html, assets),
  );
}

/** Upload + rewrite tokens for every `html` block, returning a new block array
 * whose html is self-contained (CDN URLs, canonical embed tokens). Throws if any
 * upload fails or a token can't be resolved, so Save aborts rather than
 * persisting a broken page (see {@link uploadAndRewriteTokens}). */
async function uploadAndRewriteBlocks(
  blocks: PageBlock[],
  assets: Map<string, LocalAsset>,
  embeds: Map<string, LocalEmbed>,
  createAssetUploadUrl: CreateAssetUploadUrl,
): Promise<PageBlock[]> {
  const out: PageBlock[] = [];
  for (const block of blocks) {
    if (block.type !== "html") {
      out.push(block);
      continue;
    }
    const html = await uploadAndRewriteTokens(block.html, assets, embeds, createAssetUploadUrl);
    out.push({ type: "html", html });
  }
  return out;
}

function ComposeRoute() {
  // Gate the whole composer behind the LLM_TTW_COMPOSER feature flag. When off
  // (including when the flag has never been set), render the 404 page instead of
  // the composer — matching the app's convention for a hidden route.
  const llmTtwComposer = useFeatureFlag("LLM_TTW_COMPOSER");
  if (!llmTtwComposer) return <Redirect to="/$" />;

  return <ComposeRouteInner />;
}

function ComposeRouteInner() {
  const { data: userInfoRows } = useSuspenseQuery(tableQueryOptions(tables.currentUserInfo));
  const userInfo = userInfoRows[0];
  // The composer's education tour is acked per underwritable entity, exactly
  // like the dashboard tour. `smb_super_view` is the caller-scoped read that
  // carries both the entity id and this user's acknowledgment rows; the composer
  // is only reachable once a shareable card exists, so the view is populated.
  const { data: superViewRows } = useSuspenseQuery(tableQueryOptions(tables.smbSuperView));
  const superView = superViewRows[0];
  // Rehydrate the caller's persisted (first/only) chat so a refresh doesn't lose
  // the conversation. Lazy (non-suspense) so we can gate the initial render on it
  // and avoid a greeting-then-history flash.
  const chatQuery = useProcedureQueryLazy(procedures.getComposerChat);
  // Also load the caller's last SAVED page (CDN URLs, self-contained). After a
  // refresh the transcript's page still carries session-only media tokens, so we
  // fall back to this so a restored page renders and can be re-saved.
  const savedPageQuery = useProcedureQueryLazy(procedures.getMyComposerPage);

  if (chatQuery.isLoading || savedPageQuery.isLoading) {
    return (
      <div className="flex h-dvh flex-col">
        <DefaultNavbar />
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <Spinner size="medium" />
        </div>
      </div>
    );
  }

  const savedBody = savedPageQuery.data?.body;
  const restoredPageBlocks =
    savedBody && savedBody.length > 0 ? parseBlocksPayload(savedBody) : null;
  const tour = superView
    ? {
        underwritableEntityId: superView.underwritableEntityId,
        isAcked: superView.userUwAcknowledgments.some(
          (ack) => ack.messageKey === MESSAGE_KEYS.ttwComposerTour,
        ),
      }
    : null;
  return (
    <ComposeWorkspace
      firstName={userInfo?.firstName}
      persisted={chatQuery.data?.messages ?? []}
      restoredPageBlocks={
        restoredPageBlocks && restoredPageBlocks.length > 0 ? restoredPageBlocks : null
      }
      tour={tour}
    />
  );
}

function ComposeWorkspace({
  firstName,
  persisted,
  restoredPageBlocks,
  tour,
}: {
  firstName: string | null | undefined;
  persisted: { role: string; content: string }[];
  /** The caller's last saved page as a block array (CDN URLs), or null if none.
   * Used as the preview + Save fallback when the transcript's page has
   * unresolvable media. */
  restoredPageBlocks: PageBlock[] | null;
  /** Education-tour context (entity to ack against + whether already dismissed),
   * or null if the caller has no resolvable business (tour is then skipped). */
  tour: { underwritableEntityId: Uuid; isAcked: boolean } | null;
}) {
  // Seed the conversation once. A fresh chat starts with just the greeting; a
  // rehydrated chat replays its persisted transcript, which already carries the
  // greeting as its first turn (it's sent to and persisted by the backend), so
  // we don't re-prepend it. Lazy initializer so it isn't rebuilt each render.
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const rehydrated = messagesFromPersisted(persisted);
    return rehydrated.length > 0 ? rehydrated : [greetingMessage(firstName)];
  });
  const [input, setInput] = useState("");
  // The chat pane element the education tour's closing tooltip anchors to. In
  // state (not a ref) so the tour re-renders once the pane mounts.
  const [chatAnchor, setChatAnchor] = useState<HTMLElement | null>(null);
  // Pending media dropped but not yet attached to a sent message.
  const [pendingAssets, setPendingAssets] = useState<LocalAsset[]>([]);
  // Every asset ever dropped this session, keyed by id, so the preview can
  // resolve tokens from earlier turns too. Kept in a ref (blob URLs are stable).
  const assetsRef = useRef<Map<string, LocalAsset>>(new Map());
  // Every pasted embed this session, keyed by id, for preview token resolution.
  const embedsRef = useRef<Map<string, LocalEmbed>>(new Map());

  const { mutateAsync: generatePage, isPending } = useProcedureMutation(
    procedures.generateComposerPage,
  );
  const { mutateAsync: savePage, isPending: isSaving } = useProcedureMutation(
    procedures.saveComposerPage,
  );
  const { mutateAsync: createAssetUploadUrl } = useProcedureMutation(
    procedures.createComposerAssetUploadUrl,
  );

  // Revoke object URLs on unmount to avoid leaking browser memory.
  useEffect(() => {
    const assets = assetsRef.current;
    return () => {
      for (const asset of assets.values()) URL.revokeObjectURL(asset.objectUrl);
    };
  }, []);

  // The most recent page the assistant produced this conversation, as a block
  // array. Conversation-only turns (no `blocks`) don't change it, so an early
  // Q&A leaves the preview empty and later refinements swap in the newest page.
  const latestTurnBlocks = useMemo(
    () => [...messages].reverse().find((message) => message.blocks)?.blocks ?? null,
    [messages],
  );

  // After a refresh the transcript restores blocks with the original media
  // tokens, but the browser-local files are gone. When the current page
  // references media we can't resolve, fall back to the last SAVED page (CDN
  // URLs, self-contained) so the preview renders AND Save works without
  // re-attaching. In the normal in-session flow the tokens resolve, so the live
  // transcript page wins.
  const previewBlocks =
    latestTurnBlocks !== null && blocksHaveUnresolvableMedia(latestTurnBlocks, assetsRef.current)
      ? (restoredPageBlocks ?? latestTurnBlocks)
      : (latestTurnBlocks ?? restoredPageBlocks);

  // Per-`html`-block preview transform: pull embeds out to render as React
  // iframes, resolve media tokens to browser-local `blob:` URLs, and run the
  // sanity pass (strips scripts/handlers/js: URLs) before the fragment is
  // inlined into the composed-page container.
  const previewCtx: BlockRenderContext = {
    prepareHtml: (rawHtml) => {
      const { html, embedUrls } = extractPreviewEmbeds(rawHtml, embedsRef.current);
      return { html: sanitizeComposerHtml(resolveMediaTokens(html, assetsRef.current)), embedUrls };
    },
    renderCommunityQuotes: () => previewCommunityQuotes(),
  };

  function addAssets(files: FileList | File[]) {
    const mediaFiles = Array.from(files).filter(
      (file) => file.type.startsWith("image/") || file.type.startsWith("video/"),
    );
    if (mediaFiles.length === 0) return;
    const oversized = mediaFiles.find(
      (file) => file.type.startsWith("video/") && file.size > MAX_VIDEO_BYTES,
    );
    if (oversized) {
      toaster({ message: "Videos must be under 50 MB.", variant: "error" });
      return;
    }
    const next = mediaFiles.map(makeLocalAsset);
    for (const asset of next) assetsRef.current.set(asset.id, asset);
    setPendingAssets((prev) => [...prev, ...next]);
  }

  function removePendingAsset(id: string) {
    setPendingAssets((prev) => prev.filter((asset) => asset.id !== id));
    const asset = assetsRef.current.get(id);
    if (asset) {
      URL.revokeObjectURL(asset.objectUrl);
      assetsRef.current.delete(id);
    }
  }

  async function handleSend() {
    await sendMessage(input.trim());
  }

  /**
   * Send a user turn built from `text` plus any pending media. Shared by the
   * textarea Send button (`handleSend`, text = the typed input) and the
   * suggested-message chips (text = the chip's message). Clears the input and
   * pending-asset trays, so a suggestion sends exactly as a typed message would.
   */
  async function sendMessage(text: string) {
    const trimmed = text.trim();
    if ((!trimmed && pendingAssets.length === 0) || isPending) return;

    // Detect pasted YouTube/Vimeo URLs in the message: mint an `{{embed:<id>}}`
    // token for each (recorded for preview resolution) and strip the raw URL from
    // the prose so the model sees a clean placeholder, like image tokens.
    let prose = trimmed;
    const embedTokens: string[] = [];
    for (const word of trimmed.split(/\s+/)) {
      const parsed = parseEmbedUrl(word);
      if (!parsed) continue;
      const id = makeLocalId();
      const embed: LocalEmbed = { id, token: `{{embed:${id}}}`, ...parsed };
      embedsRef.current.set(id, embed);
      embedTokens.push(embed.token);
      prose = prose.replace(word, "").trim();
    }

    // Append the media + embed tokens to the user's text so the model knows which
    // placeholders to embed and roughly where.
    const tokenText = [...pendingAssets.map((asset) => asset.token), ...embedTokens].join(" ");
    const content = [prose, tokenText].filter(Boolean).join("\n\n");

    const userMessage: ChatMessage = {
      role: "user",
      content,
      attachments: pendingAssets.length > 0 ? pendingAssets : undefined,
    };
    const nextMessages: ChatMessage[] = [...messages, userMessage];
    setMessages(nextMessages);
    setInput("");
    setPendingAssets([]);

    await requestReply(nextMessages);
  }

  /**
   * Call the model with the given conversation and append its reply. On failure
   * (e.g. the OpenAI call times out server-side and the procedure throws) append
   * an in-chat error bubble with a "Try again" affordance instead of leaving the
   * owner staring at an unanswered message — a timeout should degrade, never
   * dead-end. The error is still reported to Sentry/logs.
   */
  async function requestReply(baseMessages: ChatMessage[]) {
    try {
      // Send the full conversation, including the seeded greeting (index 0), so
      // the model sees its own opening question and picks up from the owner's
      // answer. Replay assistant turns in the fenced form the model emits so it
      // sees its own prior prose + HTML. The greeting is persisted as the first
      // turn (see the backend), so a rehydrated chat carries it rather than
      // re-prepending it. Error bubbles are UI-only and excluded from the replay.
      const conversation = baseMessages
        .filter((message) => !message.error)
        .map((message) =>
          message.role === "assistant"
            ? { role: message.role, content: assistantTurnForModel(message) }
            : { role: message.role, content: message.content },
        );
      const reply = await generatePage({ messages: conversation });
      // Be defensive about the wire shape: an older deployed module (or a future
      // one) might omit a field. Never let a missing `message`/`body` crash the
      // render (e.g. `content.trim()` on undefined).
      const message = reply.message ?? "";
      const body = reply.body ?? "";
      // An empty body means "no page change this turn" — keep `blocks` undefined
      // so the preview holds the prior page. A body that parses to nothing is
      // treated the same way.
      const blocks = body.length > 0 ? parseBlocksPayload(body) : [];
      setMessages([
        ...baseMessages,
        {
          role: "assistant",
          content: message,
          blocks: blocks.length > 0 ? blocks : undefined,
        },
      ]);
    } catch (error) {
      ErrorHandler.reportError(error);
      setMessages([
        ...baseMessages,
        {
          role: "assistant",
          content:
            "Sorry — I couldn't generate a response just then (that request took too long). Please try again.",
          error: true,
        },
      ]);
    }
  }

  /**
   * Re-run the last generation after an error. Drops the trailing error
   * bubble(s) and re-requests from the conversation up to and including the last
   * real turn, so the retry sends exactly what the failed attempt did.
   */
  async function handleRetry() {
    if (isPending) return;
    let base = messages;
    while (base.length > 0 && base[base.length - 1]?.error) {
      base = base.slice(0, -1);
    }
    if (base.length === 0) return;
    setMessages(base);
    await requestReply(base);
  }

  async function handleSave() {
    if (previewBlocks === null || isSaving) return;
    try {
      // Upload every browser-local asset referenced in each html block, then
      // rewrite its `{{img:…}}` / `{{video:…}}` token → the CDN URL, and each
      // `{{embed:<id>}}` → `{{embed:<embedUrl>}}`, so the persisted page is
      // self-contained (no session-scoped blobs/ids). Serialize the block array
      // to the wire `body`. The backend rejects any leftover media token, so a
      // failed upload surfaces as an error, not a broken public page.
      const rewritten = await uploadAndRewriteBlocks(
        previewBlocks,
        assetsRef.current,
        embedsRef.current,
        createAssetUploadUrl,
      );
      const { cardId } = await savePage({ body: serializeBlocks(rewritten), title: "My page" });
      toaster({
        message: `Saved. Your page is live at /ttw/${cardId.toString()}`,
        variant: "success",
      });
    } catch (error) {
      ErrorHandler.default(error);
    }
  }

  return (
    <div className="flex h-dvh flex-col">
      <DefaultNavbar />
      <div className="flex min-h-0 flex-1">
        <PreviewPane
          blocks={previewBlocks}
          ctx={previewCtx}
          isGenerating={isPending && previewBlocks === null}
        />
        <ChatSidebar
          ref={setChatAnchor}
          messages={messages}
          input={input}
          pendingAssets={pendingAssets}
          isPending={isPending}
          canSave={previewBlocks !== null}
          isSaving={isSaving}
          onInputChange={setInput}
          onSend={() => void handleSend()}
          onSendSuggestion={(message) => void sendMessage(message)}
          onSave={() => void handleSave()}
          onRetry={() => void handleRetry()}
          onAddAssets={addAssets}
          onRemovePendingAsset={removePendingAsset}
        />
      </div>
      {tour ? (
        <ComposerTour
          underwritableEntityId={tour.underwritableEntityId}
          isAcked={tour.isAcked}
          chatAnchor={chatAnchor}
        />
      ) : null}
    </div>
  );
}

function PreviewPane({
  blocks,
  ctx,
  isGenerating,
}: {
  blocks: PageBlock[] | null;
  ctx: BlockRenderContext;
  isGenerating: boolean;
}) {
  // Mirror the public page's community-quotes fallback: when the page has no
  // communityQuotes block, still show the section below so the owner sees the
  // full composition their published page will carry.
  const hasQuotesBlock = blocks?.some((block) => block.type === "communityQuotes") ?? false;
  return (
    <main className="flex min-w-0 flex-1 items-center justify-center bg-black/5 p-4">
      {blocks !== null ? (
        <div className="h-full w-full overflow-y-auto rounded-none border bg-white shadow-sm">
          {/* The block renderer stacks the composed html sections + live
              components in one scroll (media tokens already resolved to blob:
              URLs by ctx.prepareHtml). */}
          <ComposedBlocks blocks={blocks} ctx={ctx} />
          {!hasQuotesBlock ? <ComposedTestimonialsPreview /> : null}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 text-center text-muted-foreground">
          {isGenerating ? (
            <>
              <Spinner size="medium" />
              <p className="text-sm">Building your page…</p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-withus-dark-blue">No preview yet</p>
              <p className="max-w-xs text-sm">
                Describe the investment page you want in the chat and it'll appear here.
              </p>
            </>
          )}
        </div>
      )}
    </main>
  );
}

function ChatSidebar({
  ref,
  messages,
  input,
  pendingAssets,
  isPending,
  canSave,
  isSaving,
  onInputChange,
  onSend,
  onSendSuggestion,
  onSave,
  onRetry,
  onAddAssets,
  onRemovePendingAsset,
}: {
  /** The root `<aside>` element, exposed so the education tour can anchor its
   * closing tooltip to the chat pane. */
  ref?: React.Ref<HTMLElement>;
  messages: ChatMessage[];
  input: string;
  pendingAssets: LocalAsset[];
  isPending: boolean;
  canSave: boolean;
  isSaving: boolean;
  onInputChange: (value: string) => void;
  onSend: () => void;
  /** Send a suggested-message chip's text as a user turn (see SuggestedMessages). */
  onSendSuggestion: (message: string) => void;
  onSave: () => void;
  onRetry: () => void;
  onAddAssets: (files: FileList | File[]) => void;
  onRemovePendingAsset: (id: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, isPending]);

  return (
    <aside ref={ref} className="flex w-96 shrink-0 flex-col border-l bg-background">
      <header className="flex items-start justify-between gap-2 border-b px-4 py-3">
        <div>
          <h1 className="withus-strong text-lg">Compose your page</h1>
          <p className="text-xs text-muted-foreground">
            Chat to build and refine your public investment page.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onSave}
          disabled={!canSave}
          isLoading={isSaving}
        >
          Save
        </Button>
      </header>

      <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
        {messages.map((message, index) =>
          message.role === "user" ? (
            <UserBubble key={index} content={message.content} attachments={message.attachments} />
          ) : (
            <AssistantBubble
              key={index}
              content={message.content}
              // The seeded greeting carries the starter page as blocks, but
              // that's not a model build — don't show "Updated the preview →".
              updatedPreview={message.blocks !== undefined && message.isGreeting !== true}
              error={message.error === true}
              // Only the newest bubble gets a retry — retrying an older error
              // would discard everything after it.
              onRetry={
                message.error === true && index === messages.length - 1 ? onRetry : undefined
              }
            />
          ),
        )}

        {isPending ? (
          <div className="flex items-center gap-2 self-start text-sm text-muted-foreground">
            <Spinner size="small" className="size-4" />
            Generating…
          </div>
        ) : null}
      </div>

      <div
        className={cn("border-t p-3", isDragging && "bg-withus-blue/5 ring-2 ring-withus-blue/40")}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          onAddAssets(event.dataTransfer.files);
        }}
      >
        <SuggestedMessages messages={messages} disabled={isPending} onSend={onSendSuggestion} />

        {pendingAssets.length > 0 ? (
          <div className="mb-2 flex flex-wrap gap-2">
            {pendingAssets.map((asset) => (
              <PendingAssetChip
                key={asset.id}
                asset={asset}
                onRemove={() => onRemovePendingAsset(asset.id)}
              />
            ))}
          </div>
        ) : null}

        <Textarea
          value={input}
          onChange={(event) => onInputChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              onSend();
            }
          }}
          placeholder="Describe your page, drag in images or video, paste a YouTube link, or ask for a change…"
          rows={3}
          disabled={isPending}
          className="mb-2 resize-none"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(event) => {
            if (event.target.files) onAddAssets(event.target.files);
            event.target.value = "";
          }}
        />
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="shrink-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={isPending}
          >
            Add media
          </Button>
          <Button
            className="flex-1"
            onClick={onSend}
            isLoading={isPending}
            disabled={input.trim().length === 0 && pendingAssets.length === 0}
          >
            Send
          </Button>
        </div>
      </div>
    </aside>
  );
}

/**
 * Dashed-outline "suggested message" chips shown above the chat input. They
 * guide the owner toward capabilities (upload a logo, recolor, remove a section)
 * and hand a lazy owner a concrete next thing to say. Clicking a chip sends its
 * text as a user turn via `onSend`, identical to typing it.
 *
 * The set is phase-aware (`pickSuggestions` reads the transcript) so early chips
 * cover the basics and later ones surface editing/finishing capabilities. Hidden
 * while a reply is generating — a suggestion mid-generation would be ignored by
 * the send guard anyway, and an empty row reads cleaner than dead chips.
 */
function SuggestedMessages({
  messages,
  disabled,
  onSend,
}: {
  messages: ChatMessage[];
  disabled: boolean;
  onSend: (message: string) => void;
}) {
  const suggestions = useMemo(() => pickSuggestions(messages), [messages]);
  if (disabled || suggestions.length === 0) return null;

  return (
    <div className="mb-2 flex flex-wrap gap-1.5">
      {suggestions.map((suggestion) => (
        <button
          key={suggestion.message}
          type="button"
          onClick={() => onSend(suggestion.message)}
          className="rounded-full border border-dashed border-withus-blue/50 px-2.5 py-1 text-left text-xs text-muted-foreground transition-colors hover:border-withus-blue hover:bg-withus-blue/5 hover:text-foreground"
        >
          {suggestion.message}
        </button>
      ))}
    </div>
  );
}

function PendingAssetChip({ asset, onRemove }: { asset: LocalAsset; onRemove: () => void }) {
  return (
    <div className="relative size-16 overflow-hidden rounded-md border">
      {asset.kind === "video" ? (
        <video src={asset.objectUrl} muted className="size-full object-cover" />
      ) : (
        <img src={asset.objectUrl} alt={asset.fileName} className="size-full object-cover" />
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${asset.fileName}`}
        className="absolute top-0.5 right-0.5 flex size-5 items-center justify-center rounded-full bg-black/60 text-xs leading-none text-white"
      >
        ×
      </button>
    </div>
  );
}

function UserBubble({ content, attachments }: { content: string; attachments?: LocalAsset[] }) {
  // Hide raw media/embed tokens from the chat bubble — show thumbnails instead
  // so the conversation reads cleanly.
  const displayText = content.replace(/\{\{(?:img|video|embed):[^}]+\}\}/g, "").trim();
  return (
    <div className="max-w-[85%] self-end rounded-md bg-withus-dark-blue px-3 py-2 text-sm whitespace-pre-wrap text-withus-pure-white">
      {displayText ? <p>{displayText}</p> : null}
      {attachments && attachments.length > 0 ? (
        <div className={cn("flex flex-wrap gap-1.5", displayText && "mt-2")}>
          {attachments.map((asset) =>
            asset.kind === "video" ? (
              <video
                key={asset.id}
                src={asset.objectUrl}
                muted
                className="size-14 rounded object-cover"
              />
            ) : (
              <img
                key={asset.id}
                src={asset.objectUrl}
                alt={asset.fileName}
                className="size-14 rounded object-cover"
              />
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * An assistant chat turn. Shows the conversational prose, and — when this turn
 * also (re)built the page — a small note that the preview was updated.
 */
function AssistantBubble({
  content,
  updatedPreview,
  error = false,
  onRetry,
}: {
  content: string;
  updatedPreview: boolean;
  error?: boolean;
  /** Present only on a retryable error bubble (the newest one). */
  onRetry?: () => void;
}) {
  const text = (content ?? "").trim();
  return (
    <div className="flex max-w-[85%] flex-col gap-1 self-start">
      {text ? (
        <div
          className={cn(
            "rounded-md px-3 py-2 text-sm whitespace-pre-wrap",
            error
              ? "border border-destructive/30 bg-destructive/10 text-destructive"
              : "bg-muted text-foreground",
          )}
        >
          {text}
        </div>
      ) : null}
      {error && onRetry ? (
        <Button variant="outline" size="sm" className="self-start" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
      {updatedPreview ? (
        <p className="pl-1 text-xs text-muted-foreground">Updated the preview →</p>
      ) : null}
    </div>
  );
}
